import { Checkbox, Popover } from "@shopify/polaris"
import { useContext, useState } from "react"
import { GlobalStateContext } from "../providers/GlobalStateProvider.jsx"
import { useTranslation } from "react-i18next"

export const ThemePicker = ({
	disabled,
	themeIdSelected,
	onThemeSelected,
	publishThemeSelected,
	setPublishThemeSelected,
}) => {
	const { state } = useContext(GlobalStateContext)
	const { t } = useTranslation()
	const [themePickerPopoverActive, setThemePickerPopoverActive] =
		useState(false)

	const togglePopover = (e) => {
		e.stopPropagation()
		setThemePickerPopoverActive(!themePickerPopoverActive)
	}

	const themeSelectedName =
		state.storeThemes?.find((theme) => theme.id === themeIdSelected)
			?.name || ""

	const themeSelectedTitle =
		state.storeThemes?.find((theme) => theme.id === themeIdSelected)
			?.title || ""

	const compatibleAuthors = ["AutoStore.ai", "LuminTheme", "KopyTheme"].map(
		(author) => author.toLowerCase(),
	)
	const autoStoreThemes = state.storeThemes.filter((theme) =>
		compatibleAuthors.includes(theme.author?.toLowerCase()),
	)
	if (autoStoreThemes.length === 0) return null

	return (
		<>
			<div
				style={{
					background: "#222",
					color: "#fff",
					borderRadius: "0.3rem",
					display: "flex",
					gap: "0.2rem",
					fontSize: "0.75rem",
					cursor: "pointer",
					width: "fit-content",
				}}
				onClick={(e) => {
					if (!disabled) {
						togglePopover(e)
					}
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						padding: "0.1rem 0.3rem",
						paddingRight: "0.1rem",
						borderRadius: "0.3rem 0rem 0rem 0.3rem",
					}}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="0.9rem"
						height="0.9rem"
						viewBox="0 0 24 24"
					>
						<path
							fill="currentColor"
							d="m2.5 19.6l1.3.6v-9L1.4 17c-.4 1.1.1 2.2 1.1 2.6M15.2 4.8l5 12l-7.3 3l-5-11.9v-.1zm.1-2c-.3 0-.5 0-.8.1L7.1 6c-.7.3-1.2 1-1.2 1.8c0 .2 0 .5.1.8l5 11.9c.3.8 1 1.2 1.8 1.2c.3 0 .5 0 .8-.1l7.4-3.1c1-.4 1.5-1.6 1.1-2.6L17.1 4c-.3-.8-1.1-1.2-1.8-1.2m-4.8 7.1c-.6 0-1-.4-1-1s.4-1 1-1s1 .5 1 1s-.4 1-1 1m-4.6 9.9c0 1.1.9 2 2 2h1.4l-3.4-8.3z"
						/>
					</svg>
				</div>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.2rem",
						padding: "0.1rem 0.3rem",
						paddingLeft: "0rem",
					}}
				>
					<span>
						Theme: <b>{themeSelectedName}</b>
						{themeSelectedTitle ? (
							<span>
								{" - "}
								<b>{themeSelectedTitle}</b>
							</span>
						) : (
							""
						)}
					</span>
					<div style={{ height: "0.85rem", width: "0.85rem" }}>
						<svg
							width={"0.9rem"}
							height={"0.9rem"}
							viewBox="0 0 512 512"
						>
							<path
								d="M128 192l128 128 128-128z"
								fill="currentColor"
							></path>
						</svg>
					</div>
				</div>
			</div>

			<Popover
				active={themePickerPopoverActive}
				activator={<div></div>}
				onClose={() => setThemePickerPopoverActive(false)}
			>
				<div
					style={{
						padding: "0.25rem",
						maxHeight: "12rem",
						maxWidth: "21rem",
						overflowY: "scroll",
						display: "flex",
						flexDirection: "column",
						gap: "0.25rem",
					}}
				>
					<style>{`
						.theme-option:hover {
							background: #f0f0f0;
						}
					`}</style>

					<div
						style={{
							paddingLeft: "0.25rem",
							paddingBottom: "0.3rem",
							marginBottom: "0.3rem",
							borderBottom: "1px solid #e0e0e0",
						}}
					>
						<Checkbox
							label={
								<p style={{ fontSize: "0.85rem" }}>
									{t("kopy_pages.publish_theme_selected")}
								</p>
							}
							checked={publishThemeSelected}
							onChange={() =>
								setPublishThemeSelected(!publishThemeSelected)
							}
						/>
					</div>

					{state.storeThemes.length === 20 ? (
						<div
							style={{
								backgroundColor: "#ffb800",
								color: "#222",
								padding: "0.5rem",
								borderRadius: "0.5rem",
								fontSize: "0.7rem",
								lineHeight: "1.1",
							}}
						>
							<p style={{ marginBottom: "0.3rem" }}>
								{t(
									"kopy_pages.you_have_reached_the_maximum_number_of_themes_you_can_install",
								)}
							</p>
							<p>
								{t(
									"kopy_pages.we_suggest_that_you_remove_an_unused_theme",
								)}
							</p>
						</div>
					) : null}

					{autoStoreThemes
						?.sort(
							(a, b) =>
								new Date(b.createdAt) - new Date(a.createdAt),
						)
						.map((theme) => {
							const selected = theme.id === themeIdSelected
							return (
								<div
									key={theme.id}
									className="theme-option"
									onClick={() => onThemeSelected(theme.id)}
									style={{
										display: "flex",
										alignItems: "center",
										gap: "0.5rem",
										cursor: "pointer",
										padding: "0.2rem 0.5rem",
										paddingLeft: "0.2rem",
										borderRadius: "0.5rem",
									}}
								>
									<div
										style={{
											flexShrink: 0,
											backgroundColor:
												theme.role === "MAIN"
													? "#5371ff"
													: "#222",
											color: "#fff",
											padding: "0.1rem 0.4rem",
											borderRadius: "0.5rem",
											fontSize: "0.7rem",
											fontWeight: "500",
											textTransform: "capitalize",
										}}
									>
										{theme.role === "MAIN"
											? t("kopy_pages.theme_active")
											: t("kopy_pages.theme_draft")}
									</div>
									<p
										style={{
											flexGrow: 1,
											fontSize: "0.85rem",
											fontWeight: "500",
											textTransform: "capitalize",
										}}
									>
										{theme.name ? (
											<b>
												{theme.name}
												{" - "}
											</b>
										) : (
											""
										)}
										{theme.title}
									</p>

									<div
										style={{
											flexShrink: 0,
											width: "0.5rem",
											height: "0.5rem",
											backgroundColor: selected
												? "#5371ff"
												: "transparent",
											borderRadius: "50%",
										}}
									></div>
								</div>
							)
						})}
				</div>
			</Popover>
		</>
	)
}
